import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getAttributionPayload, captureUtm } from "@/lib/attribution";
import { canTrackAnalytics, CONSENT_EVENT } from "@/lib/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "sm_sid";

function getOrCreateSessionId() {
  if (typeof window === "undefined") return "server";
  let sid = sessionStorage.getItem(KEY);
  if (!sid) {
    sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    sessionStorage.setItem(KEY, sid);
  }
  return sid;
}

/**
 * Tracks a pageview every time the route changes. Admin and login pages are
 * skipped so the stats reflect real visitors, not our own sessions.
 * Analytics tracking requires explicit GDPR consent (analytical category).
 */
export default function PageViewTracker() {
  const location = useLocation();
  const [consentTick, setConsentTick] = useState(0);

  // Re-run the tracking effect whenever consent changes (e.g. user accepts
  // analytical cookies later in the same session).
  useEffect(() => {
    const onChange = () => setConsentTick((n) => n + 1);
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/login")) return;
    if (!canTrackAnalytics()) return;

    // Persist UTM on first visit / when new UTMs appear in URL
    captureUtm();
    const attr = getAttributionPayload();

    const payload = {
      session_id: getOrCreateSessionId(),
      path,
      referrer: document.referrer || null,
      timezone:
        Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || null,
      language: navigator.language || null,
      screen_width: window.innerWidth,
      visitor_id: attr.visitor_id,
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
    };

    axios.post(`${API}/analytics/track`, payload).catch(() => {});
  }, [location.pathname, consentTick]);

  return null;
}
